import React, {
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  useTranslation,
} from 'react-i18next';


const DEFAULT_POSITION = {
  lat: 35.19879267098201,
  lng: 128.68738105465292,
};


function normalizeLanguage(
  value,
) {
  const language =
    String(
      value ||
      'ko',
    )
      .trim()
      .toLowerCase()
      .split('-')[0];

  if (
    language ===
    'zh'
  ) {
    return 'zh-CN';
  }

  return (
    language ||
    'ko'
  );
}


function loadGoogleMaps(
  language,
) {
  if (
    window.google?.maps
  ) {
    return Promise.resolve(
      window.google,
    );
  }

  const existingScript =
    document.querySelector(
      'script[src*="maps.googleapis.com/maps/api/js"]',
    );

  if (
    existingScript
  ) {
    return new Promise(
      (
        resolve,
        reject,
      ) => {
        const checkLoaded =
          () => {
            if (
              window.google?.maps
            ) {
              resolve(
                window.google,
              );

              return;
            }

            reject(
              new Error(
                'GOOGLE_MAPS_LOAD_FAILED',
              ),
            );
          };

        existingScript.addEventListener(
          'load',
          checkLoaded,
          {
            once:
              true,
          },
        );

        existingScript.addEventListener(
          'error',
          () => {
            reject(
              new Error(
                'GOOGLE_MAPS_LOAD_FAILED',
              ),
            );
          },
          {
            once:
              true,
          },
        );

        window.setTimeout(
          () => {
            if (
              window.google?.maps
            ) {
              resolve(
                window.google,
              );

              return;
            }

            reject(
              new Error(
                'GOOGLE_MAPS_LOAD_FAILED',
              ),
            );
          },
          10000,
        );
      },
    );
  }

  const apiKey =
    String(
      process.env
        .REACT_APP_GOOGLE_MAPS_API_KEY ||
      '',
    ).trim();

  if (
    !apiKey
  ) {
    return Promise.reject(
      new Error(
        'GOOGLE_MAPS_API_KEY_MISSING',
      ),
    );
  }

  return new Promise(
    (
      resolve,
      reject,
    ) => {
      const script =
        document.createElement(
          'script',
        );

      const locale =
        normalizeLanguage(
          language,
        );

      script.src =
        `https://maps.googleapis.com/maps/api/js` +
        `?key=${encodeURIComponent(
          apiKey,
        )}` +
        `&libraries=places` +
        `&language=${encodeURIComponent(
          locale,
        )}`;

      script.async =
        true;

      script.defer =
        true;

      script.onload =
        () => {
          if (
            window.google?.maps
          ) {
            resolve(
              window.google,
            );

            return;
          }

          reject(
            new Error(
              'GOOGLE_MAPS_LOAD_FAILED',
            ),
          );
        };

      script.onerror =
        () => {
          reject(
            new Error(
              'GOOGLE_MAPS_LOAD_FAILED',
            ),
          );
        };

      document.head.appendChild(
        script,
      );
    },
  );
}


function getCountry(
  addressComponents,
) {
  if (
    !Array.isArray(
      addressComponents,
    )
  ) {
    return '';
  }

  const component =
    addressComponents.find(
      (
        item,
      ) =>
        Array.isArray(
          item.types,
        ) &&
        item.types.includes(
          'country',
        ),
    );

  return (
    component?.short_name ||
    ''
  );
}


function createPosition(
  location,
) {
  if (
    !location
  ) {
    return null;
  }

  const lat =
    typeof location.lat ===
    'function'
      ? location.lat()
      : Number(
          location.lat,
        );

  const lng =
    typeof location.lng ===
    'function'
      ? location.lng()
      : Number(
          location.lng,
        );

  if (
    !Number.isFinite(
      lat,
    ) ||
    !Number.isFinite(
      lng,
    )
  ) {
    return null;
  }

  return {
    lat,
    lng,
  };
}


function createMapInfo(
  result,
  fallbackPosition,
) {
  const position =
    createPosition(
      result?.geometry?.location,
    ) ||
    createPosition(
      fallbackPosition,
    );

  return {
    address:
      result?.formatted_address ||
      result?.name ||
      '',

    country:
      getCountry(
        result?.address_components,
      ),

    position,

    placeID:
      result?.place_id ||
      '',
  };
}


export default function GoogleAddressPicker({
  open,
  initialAddress,
  onClose,
  onSelect,
}) {
  const {
    t,
    i18n,
  } = useTranslation(
    'app',
  );

  const activeLanguage =
    normalizeLanguage(
      i18n.resolvedLanguage ||
      i18n.language,
    );

  const activeLanguageRef =
    useRef(
      activeLanguage,
    );

  activeLanguageRef.current =
    activeLanguage;

  const mapElementRef =
    useRef(
      null,
    );

  const searchInputRef =
    useRef(
      null,
    );

  const mapRef =
    useRef(
      null,
    );

  const markerRef =
    useRef(
      null,
    );

  const geocoderRef =
    useRef(
      null,
    );

  const mapClickListenerRef =
    useRef(
      null,
    );

  const autocompleteServiceRef =
    useRef(
      null,
    );

  const placesServiceRef =
    useRef(
      null,
    );

  const sessionTokenRef =
    useRef(
      null,
    );

  const geocodeAddressRef =
    useRef(
      null,
    );

  const geocodePositionRef =
    useRef(
      null,
    );

  const getPlaceDetailsRef =
    useRef(
      null,
    );

  const predictionRequestRef =
    useRef(
      0,
    );

  const detailsRequestRef =
    useRef(
      0,
    );

  const geocodeRequestRef =
    useRef(
      0,
    );

  const selectedPlaceIDRef =
    useRef(
      '',
    );

  const pendingPositionRef =
    useRef(
      null,
    );

  const previousLanguageRef =
    useRef(
      activeLanguage,
    );

  const [
    searchQuery,
    setSearchQuery,
  ] = useState(
    '',
  );

  const [
    isQueryActive,
    setIsQueryActive,
  ] = useState(
    false,
  );

  const [
    predictions,
    setPredictions,
  ] = useState(
    [],
  );

  const [
    highlightedPrediction,
    setHighlightedPrediction,
  ] = useState(
    -1,
  );

  const [
    servicesReady,
    setServicesReady,
  ] = useState(
    false,
  );

  const [
    selectedInfo,
    setSelectedInfo,
  ] = useState(
    null,
  );

  const selectedInfoRef =
    useRef(
      selectedInfo,
    );

  const selectedInfoLanguageRef =
    useRef(
      '',
    );

  selectedInfoRef.current =
    selectedInfo;

  const [
    isLoading,
    setIsLoading,
  ] = useState(
    false,
  );

  const [
    loadError,
    setLoadError,
  ] = useState(
    false,
  );


  useEffect(
    () => {
      if (
        !open
      ) {
        return undefined;
      }

      let cancelled =
        false;

      setIsLoading(
        true,
      );

      setLoadError(
        false,
      );

      setSelectedInfo(
        null,
      );

      selectedInfoLanguageRef.current =
        '';

      setSearchQuery(
        '',
      );

      setIsQueryActive(
        false,
      );

      setPredictions(
        [],
      );

      setHighlightedPrediction(
        -1,
      );

      selectedPlaceIDRef.current =
        '';

      pendingPositionRef.current =
        null;

      const initialize =
        async () => {
          try {
            await loadGoogleMaps(
              activeLanguageRef.current,
            );

            if (
              cancelled ||
              !mapElementRef.current
            ) {
              return;
            }

            const google =
              window.google;

            const map =
              new google.maps.Map(
                mapElementRef.current,
                {
                  center:
                    DEFAULT_POSITION,

                  zoom:
                    16,

                  gestureHandling: 
                    'greedy',

                  mapTypeControl:
                    false,

                  streetViewControl:
                    false,

                  fullscreenControl:
                    false,
                },
              );

            const marker =
              new google.maps.Marker({
                map,

                position:
                  DEFAULT_POSITION,
              });

            const geocoder =
              new google.maps.Geocoder();

            mapRef.current =
              map;

            markerRef.current =
              marker;

            geocoderRef.current =
              geocoder;

            const autocompleteService =
              new google.maps.places.AutocompleteService();

            const placesService =
              new google.maps.places.PlacesService(
                map,
              );

            autocompleteServiceRef.current =
              autocompleteService;

            placesServiceRef.current =
              placesService;

            setServicesReady(
              true,
            );


            const applyResult =
              (
                result,
                fallbackPosition,
              ) => {
                const mapInfo =
                  createMapInfo(
                    result,
                    fallbackPosition,
                  );

                if (
                  !mapInfo.position
                ) {
                  return;
                }

                marker.setPosition(
                  mapInfo.position,
                );

                map.setCenter(
                  mapInfo.position,
                );

                selectedInfoRef.current = mapInfo;
                setSelectedInfo(mapInfo);

                selectedInfoLanguageRef.current =
                  activeLanguageRef.current;
              };


            const geocodePosition =
              (
                position,
                language = activeLanguageRef.current,
              ) => {
                selectedInfoRef.current = null;
                setSelectedInfo(null);
                selectedInfoLanguageRef.current = '';
                const requestID =
                  ++geocodeRequestRef.current;

                detailsRequestRef.current +=
                  1;

                pendingPositionRef.current =
                  position;

                selectedPlaceIDRef.current =
                  '';

                geocoder.geocode(
                  {
                    location:
                      position,

                    language,
                  },
                  (
                    results,
                    status,
                  ) => {
                    if (
                      requestID !==
                        geocodeRequestRef.current ||
                      language !==
                        activeLanguageRef.current
                    ) {
                      return;
                    }

                    if (
                      status !==
                        'OK' ||
                      !Array.isArray(
                        results,
                      ) ||
                      results.length ===
                        0
                    ) {
                      const fallbackInfo = {
                        address:
                          '',

                        country:
                          '',

                        position:
                          createPosition(
                            position,
                          ),

                        placeID:
                          '',
                      };
                      selectedInfoRef.current = fallbackInfo;
                      setSelectedInfo(fallbackInfo);

                      selectedInfoLanguageRef.current =
                        language;

                      marker.setPosition(
                        position,
                      );

                      return;
                    }

                    applyResult(
                      results[0],
                      position,
                    );
                  },
                );
              };

            const geocodeAddress =
              (
                address,
                language = activeLanguageRef.current,
              ) => {
                selectedInfoRef.current = null;
                setSelectedInfo(null);
                selectedInfoLanguageRef.current = '';
                const requestID =
                  ++geocodeRequestRef.current;

                detailsRequestRef.current +=
                  1;

                pendingPositionRef.current =
                  null;

                selectedPlaceIDRef.current =
                  '';

                geocoder.geocode(
                  {
                    address,

                    language,
                  },
                  (
                    results,
                    status,
                  ) => {
                    if (
                      requestID !==
                        geocodeRequestRef.current ||
                      language !==
                        activeLanguageRef.current
                    ) {
                      return;
                    }

                    if (
                      status !==
                        'OK' ||
                      !Array.isArray(
                        results,
                      ) ||
                      !results.length
                    ) {
                      return;
                    }

                    applyResult(
                      results[0],
                    );
                  },
                );
              };

            const getPlaceDetails =
              (
                placeID,
                language = activeLanguageRef.current,
              ) => {
                selectedInfoRef.current = null;
                setSelectedInfo(null);
                selectedInfoLanguageRef.current = '';
                const requestID =
                  ++detailsRequestRef.current;

                geocodeRequestRef.current +=
                  1;

                const request = {
                  placeId:
                    placeID,

                  fields: [
                    'address_components',
                    'formatted_address',
                    'geometry',
                    'name',
                    'place_id',
                  ],

                  language,
                };

                if (
                  sessionTokenRef.current
                ) {
                  request.sessionToken =
                    sessionTokenRef.current;
                }

                placesService.getDetails(
                  request,
                  (
                    place,
                    status,
                  ) => {
                    if (
                      requestID !==
                        detailsRequestRef.current ||
                      language !==
                        activeLanguageRef.current
                    ) {
                      return;
                    }

                    sessionTokenRef.current =
                      null;

                    const position =
                      createPosition(
                        place?.geometry
                          ?.location,
                      );

                    if (
                      status !==
                        google.maps.places.PlacesServiceStatus?.OK &&
                      status !==
                        'OK'
                    ) {
                      return;
                    }

                    if (
                      !position
                    ) {
                      return;
                    }

                    pendingPositionRef.current =
                      null;

                    applyResult(
                      place,
                      position,
                    );
                  },
                );
              };

            geocodeAddressRef.current =
              geocodeAddress;

            geocodePositionRef.current =
              geocodePosition;

            getPlaceDetailsRef.current =
              getPlaceDetails;


            mapClickListenerRef.current =
              map.addListener(
                'click',
                (
                  event,
                ) => {
                  const position =
                    createPosition(
                      event.latLng,
                    );

                  if (
                    !position
                  ) {
                    return;
                  }

                  setSelectedInfo(
                    null,
                  );

                  selectedInfoLanguageRef.current =
                    '';

                  setSearchQuery(
                    '',
                  );

                  setIsQueryActive(
                    false,
                  );

                  predictionRequestRef.current +=
                    1;

                  setPredictions(
                    [],
                  );

                  marker.setPosition(
                    position,
                  );

                  geocodePosition(
                    position,
                  );
                },
              );


            const normalizedInitialAddress =
              String(
                initialAddress ||
                '',
              ).trim();

            if (
              normalizedInitialAddress
            ) {
              geocodeAddress(
                normalizedInitialAddress,
              );
            }
          } catch (
            error
          ) {
            if (
              !cancelled
            ) {
              setLoadError(
                true,
              );
            }
          } finally {
            if (
              !cancelled
            ) {
              setIsLoading(
                false,
              );
            }
          }
        };


      initialize();


      return () => {
        cancelled =
          true;

        if (
          mapClickListenerRef.current
        ) {
          window.google?.maps
            ?.event
            ?.removeListener(
              mapClickListenerRef.current,
            );

          mapClickListenerRef.current =
            null;
        }

        predictionRequestRef.current +=
          1;

        detailsRequestRef.current +=
          1;

        geocodeRequestRef.current +=
          1;

        autocompleteServiceRef.current =
          null;

        placesServiceRef.current =
          null;

        geocodeAddressRef.current =
          null;

        geocodePositionRef.current =
          null;

        getPlaceDetailsRef.current =
          null;

        sessionTokenRef.current =
          null;

        setServicesReady(
          false,
        );

        mapRef.current =
          null;

        markerRef.current =
          null;

        geocoderRef.current =
          null;
      };
    },
    [
      open,
      initialAddress,
    ],
  );


  useEffect(
    () => {
      if (
        !open ||
        !isQueryActive ||
        !servicesReady ||
        !autocompleteServiceRef.current
      ) {
        return undefined;
      }

      const query =
        searchQuery.trim();

      const requestID =
        ++predictionRequestRef.current;

      if (
        query.length <
        2
      ) {
        setPredictions(
          [],
        );

        setHighlightedPrediction(
          -1,
        );

        return undefined;
      }

      const timer =
        window.setTimeout(
          () => {
            const service =
              autocompleteServiceRef.current;

            if (
              !service
            ) {
              return;
            }

            if (
              !sessionTokenRef.current &&
              window.google?.maps?.places
                ?.AutocompleteSessionToken
            ) {
              sessionTokenRef.current =
                new window.google.maps.places.AutocompleteSessionToken();
            }

            service.getPlacePredictions(
              {
                input:
                  query,

                language:
                  activeLanguage,

                sessionToken:
                  sessionTokenRef.current ||
                  undefined,
              },
              (
                results,
                status,
              ) => {
                if (
                  requestID !==
                    predictionRequestRef.current ||
                  activeLanguage !==
                    activeLanguageRef.current ||
                  query !==
                    searchQuery.trim()
                ) {
                  return;
                }

                const statusOK =
                  status ===
                    window.google?.maps?.places
                      ?.PlacesServiceStatus?.OK ||
                  status ===
                    'OK';

                setPredictions(
                  statusOK &&
                    Array.isArray(
                      results,
                    )
                    ? results
                    : [],
                );

                setHighlightedPrediction(
                  -1,
                );
              },
            );
          },
          250,
        );

      return () => {
        window.clearTimeout(
          timer,
        );

        if (
          predictionRequestRef.current ===
          requestID
        ) {
          predictionRequestRef.current +=
            1;
        }
      };
    },
    [
      open,
      servicesReady,
      isQueryActive,
      searchQuery,
      activeLanguage,
    ],
  );


  useEffect(
    () => {
      const languageChanged =
        previousLanguageRef.current !==
        activeLanguage;

      previousLanguageRef.current =
        activeLanguage;

      if (
        !open ||
        !languageChanged
      ) {
        return;
      }

      setPredictions(
        [],
      );

      setHighlightedPrediction(
        -1,
      );

      setSelectedInfo(
        null,
      );

      selectedInfoLanguageRef.current =
        '';

      if (
        isQueryActive
      ) {
        return;
      }

      if (
        selectedPlaceIDRef.current
      ) {
        getPlaceDetailsRef.current?.(
          selectedPlaceIDRef.current,
          activeLanguage,
        );

        return;
      }

      if (
        pendingPositionRef.current
      ) {
        geocodePositionRef.current?.(
          pendingPositionRef.current,
          activeLanguage,
        );

        return;
      }

      if (
        String(
          initialAddress ||
          '',
        ).trim()
      ) {
        geocodeAddressRef.current?.(
          String(
            initialAddress,
          ).trim(),
          activeLanguage,
        );

        return;
      }

      if (
        selectedInfoRef.current?.position
      ) {
        geocodePositionRef.current?.(
          selectedInfoRef.current.position,
          activeLanguage,
        );
      }
    },
    [
      open,
      activeLanguage,
      initialAddress,
      isQueryActive,
    ],
  );


  const handleSearchChange =
    (
      event,
    ) => {
      const nextQuery =
        event.target.value;

      setSearchQuery(
        nextQuery,
      );

      setIsQueryActive(
        true,
      );

      predictionRequestRef.current +=
        1;

      setSelectedInfo(
        null,
      );

      selectedInfoLanguageRef.current =
        '';

      selectedPlaceIDRef.current =
        '';

      pendingPositionRef.current =
        null;

      detailsRequestRef.current +=
        1;

      geocodeRequestRef.current +=
        1;

      setPredictions(
        [],
      );

      setHighlightedPrediction(
        -1,
      );
    };


  const selectPrediction =
    (
      prediction,
    ) => {
      const placeID =
        prediction?.place_id;

      if (
        !placeID
      ) {
        return;
      }

      predictionRequestRef.current +=
        1;

      selectedPlaceIDRef.current =
        placeID;

      pendingPositionRef.current =
        null;

      setSelectedInfo(
        null,
      );

      selectedInfoLanguageRef.current =
        '';

      setSearchQuery(
        prediction.description ||
        '',
      );

      setIsQueryActive(
        false,
      );

      setPredictions(
        [],
      );

      setHighlightedPrediction(
        -1,
      );

      getPlaceDetailsRef.current?.(
        placeID,
        activeLanguageRef.current,
      );
    };


  const handleSearchKeyDown =
    (
      event,
    ) => {
      const isComposing =
        event.nativeEvent?.isComposing ||
        event.isComposing ||
        event.keyCode ===
          229;

      if (
        isComposing
      ) {
        return;
      }

      if (
        event.key ===
        'ArrowDown'
      ) {
        if (
          predictions.length
        ) {
          event.preventDefault();

          setHighlightedPrediction(
            (current) =>
              (current + 1) %
              predictions.length,
          );
        }

        return;
      }

      if (
        event.key ===
        'ArrowUp'
      ) {
        if (
          predictions.length
        ) {
          event.preventDefault();

          setHighlightedPrediction(
            (current) =>
              current <= 0
                ? predictions.length - 1
                : current - 1,
          );
        }

        return;
      }

      if (
        event.key ===
        'Escape'
      ) {
        predictionRequestRef.current +=
          1;

        setIsQueryActive(
          false,
        );

        setPredictions(
          [],
        );

        setHighlightedPrediction(
          -1,
        );

        return;
      }

      if (
        event.key ===
        'Enter'
      ) {
        event.preventDefault();

        const prediction =
          predictions[
            highlightedPrediction >= 0
              ? highlightedPrediction
              : 0
          ];

        if (
          prediction
        ) {
          selectPrediction(
            prediction,
          );
        }
      }
    };


  if (
    !open
  ) {
    return null;
  }


  const handleConfirm =
    () => {
      const currentInfo = selectedInfoRef.current;
      if (
        isLoading ||
        !currentInfo?.position
        ||
        selectedInfoLanguageRef.current !==
          activeLanguage
      ) {
        return;
      }

      onSelect({
        address:
          currentInfo.address ||
          '',

        country:
          currentInfo.country ||
          '',

        position: {
          lat:
            Number(
              currentInfo.position.lat,
            ),

          lng:
            Number(
              currentInfo.position.lng,
            ),
        },

        placeID:
          currentInfo.placeID ||
          '',
      });
    };


  return (
    <div
      style={{
        position:
          'fixed',

        inset:
          0,

        zIndex:
          10000,

        display:
          'flex',

        alignItems:
          'center',

        justifyContent:
          'center',

        padding:
          20,

        background:
          'rgba(0, 0, 0, 0.45)',
      }}
      onMouseDown={(
        event,
      ) => {
        if (
          event.target ===
          event.currentTarget
        ) {
          onClose();
        }
      }}
    >
      <div
        style={{
          width:
            'min(900px, 100%)',

          maxHeight:
            'calc(100vh - 40px)',

          overflow:
            'hidden',

          display:
            'flex',

          flexDirection:
            'column',

          background:
            '#ffffff',

          borderRadius:
            12,

          boxShadow:
            '0 18px 50px rgba(0, 0, 0, 0.2)',
        }}
      >
        <div
          style={{
            display:
              'flex',

            alignItems:
              'center',

            justifyContent:
              'space-between',

            padding:
              '18px 20px',

            borderBottom:
              '1px solid #e5e5e5',
          }}
        >
          <strong>
            {t(
              'signup.addressPicker.title',
            )}
          </strong>

          <button
            type="button"
            onClick={
              onClose
            }
            style={{
              border:
                0,

              background:
                'transparent',

              fontSize:
                22,

              cursor:
                'pointer',
            }}
          >
            ×
          </button>
        </div>


        <div
          style={{
            padding:
              16,

            position:
              'relative',
          }}
        >
          <style>{`.pac-container { z-index: 10001 !important; }`}</style>
          <input
            ref={
              searchInputRef
            }
            type="text"
            value={
              searchQuery
            }
            onChange={
              handleSearchChange
            }
            onKeyDown={
              handleSearchKeyDown
            }
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={
              predictions.length > 0
            }
            aria-controls="signup-address-predictions"
            aria-activedescendant={
              highlightedPrediction >= 0
                ? `signup-address-prediction-${highlightedPrediction}`
                : undefined
            }
            autoComplete="off"
            placeholder={t(
              'signup.addressPicker.searchPlaceholder',
            )}
            style={{
              width:
                '100%',

              height:
                42,

              boxSizing:
                'border-box',

              padding:
                '0 12px',

              border:
                '1px solid #d9d9d9',

              borderRadius:
                6,

              outline:
                'none',
            }}
          />

          {
            predictions.length > 0 && (
              <div
                id="signup-address-predictions"
                role="listbox"
                style={{
                  position:
                    'absolute',

                  zIndex:
                    10002,

                  top:
                    'calc(100% - 16px)',

                  left:
                    16,

                  right:
                    16,

                  maxHeight:
                    240,

                  overflowY:
                    'auto',

                  background:
                    '#ffffff',

                  border:
                    '1px solid #d9d9d9',

                  borderRadius:
                    6,

                  boxShadow:
                    '0 4px 12px rgba(0, 0, 0, 0.16)',
                }}
              >
                {
                  predictions.map(
                    (
                      prediction,
                      index,
                    ) => (
                      <button
                        key={
                          prediction.place_id
                        }
                        id={`signup-address-prediction-${index}`}
                        type="button"
                        role="option"
                        aria-selected={
                          highlightedPrediction ===
                          index
                        }
                        onMouseDown={(
                          event,
                        ) => event.preventDefault()}
                        onMouseEnter={() => setHighlightedPrediction(index)}
                        onClick={() => selectPrediction(prediction)}
                        style={{
                          display:
                            'block',

                          width:
                            '100%',

                          padding:
                            '10px 12px',

                          border:
                            0,

                          borderBottom:
                            index < predictions.length - 1
                              ? '1px solid #eeeeee'
                              : 0,

                          background:
                            highlightedPrediction === index
                              ? '#f3f6fa'
                              : '#ffffff',

                          color:
                            '#222222',

                          textAlign:
                            'left',

                          cursor:
                            'pointer',
                        }}
                      >
                        {prediction.description}
                      </button>
                    ),
                  )
                }
              </div>
            )
          }
        </div>


        {
          loadError && (
            <div
              style={{
                padding:
                  '10px 20px 20px',

                color:
                  '#d32f2f',
              }}
            >
              {t(
                'signup.addressPicker.loadFailed',
              )}
            </div>
          )
        }


        <div
          ref={
            mapElementRef
          }
          style={{
            width:
              '100%',

            height:
              480,

            minHeight:
              320,

            background:
              '#eeeeee',
          }}
        />


        <div
          style={{
            padding:
              20,

            borderTop:
              '1px solid #e5e5e5',
          }}
        >
          <div
            style={{
              display:
                'grid',

              gap:
                8,

              marginBottom:
                18,

              fontSize:
                13,
            }}
          >
            <div>
              <strong>
                {t(
                  'signup.addressPicker.address',
                )}
              </strong>
              {' : '}
              {
                selectedInfo
                  ?.address ||
                '-'
              }
            </div>

            <div>
              <strong>
                {t(
                  'signup.addressPicker.country',
                )}
              </strong>
              {' : '}
              {
                selectedInfo
                  ?.country ||
                '-'
              }
            </div>

            <div>
              <strong>
                {t(
                  'signup.addressPicker.position',
                )}
              </strong>
              {' : '}
              {
                selectedInfo
                  ?.position
                  ? JSON.stringify(
                      selectedInfo.position,
                    )
                  : '-'
              }
            </div>
          </div>


          <div
            style={{
              display:
                'flex',

              justifyContent:
                'flex-end',

              gap:
                10,
            }}
          >
            <button
              type="button"
              onClick={
                onClose
              }
              style={{
                minWidth:
                  90,

                height:
                  40,

                border:
                  '1px solid #d9d9d9',

                borderRadius:
                  6,

                background:
                  '#ffffff',

                cursor:
                  'pointer',
              }}
            >
              {t(
                'signup.addressPicker.cancel',
              )}
            </button>

            <button
              type="button"
              onClick={
                handleConfirm
              }
              disabled={
                isLoading ||
                !selectedInfo
                  ?.position
              }
              style={{
                minWidth:
                  90,

                height:
                  40,

                border:
                  0,

                borderRadius:
                  6,

                background:
                  '#f37321',

                color:
                  '#ffffff',

                fontWeight:
                  700,

                cursor:
                  'pointer',
              }}
            >
              {t(
                'signup.addressPicker.confirm',
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
